// Portfolio Administration v1.0 PR 3 — sticky entity-detail header for the
// Loan page. Shows loan number + AccountState badge, the 7-pill assignment
// widget, Setup-complete state, and a back link to the list.
//
// Reunderwriting Module (Tier 3) PR 3 — header carries the "Send to
// reunderwriting" CTA. Gated by VITE_FEATURE_REUNDERWRITING_V1 + the
// Reunderwriting.Initiate permission. Clicking pops the launch modal.

import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, RefreshCw } from "lucide-react";
import { Badge, Button, Card, OverflowMenu, useToast } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useReunderwritingV1 } from "@/hooks/reunderwriting/useFeatureFlag";
import { ReunderwritingLaunchModal } from "@/components/reunderwriting/ReunderwritingLaunchModal";
import { REUNDERWRITING_PERMISSIONS } from "@/types/reunderwriting";
import { MoveToPortfolioModal } from "@/components/portfolioAdmin/portfolios/MoveToPortfolioModal";
import { ReportToComplianceModal } from "./complianceIntake/ReportToComplianceModal";
import { SevenPillAssignmentWidget } from "./SevenPillAssignmentWidget";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";

interface LoanHeaderProps {
  loan: LoanSummaryDto;
}

const ACCOUNT_STATE_COLOR: Record<string, string> = {
  performing: "bg-status-success/15 text-status-success border-status-success/30",
  "sub-performing": "bg-status-warning/15 text-status-warning border-status-warning/30",
  "non-performing": "bg-status-warning/15 text-status-warning border-status-warning/30",
  defaulted: "bg-status-error/15 text-status-error border-status-error/30",
  "charged-off": "bg-gray-mid/15 text-gray-mid border-gray-mid/30",
  liquidated: "bg-gray-mid/15 text-gray-mid border-gray-mid/30",
  "paid-off": "bg-status-info/15 text-status-info border-status-info/30",
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

export function LoanHeader({ loan }: LoanHeaderProps) {
  const stateClass =
    (loan.accountState && ACCOUNT_STATE_COLOR[loan.accountState]) ??
    "bg-gray-mid/15 text-gray-mid border-gray-mid/30";

  const reunderwritingOn = useReunderwritingV1();
  const auth = useAuth();
  const toast = useToast();
  const canInitiateReunderwriting =
    reunderwritingOn &&
    (auth.user?.permissions ?? []).includes(REUNDERWRITING_PERMISSIONS.Initiate);
  const [launchOpen, setLaunchOpen] = useState(false);

  const canManagePortfolio =
    auth.hasRoleCode("ADMIN") || auth.hasPermission("Loans.Manage");
  const [portfolioModal, setPortfolioModal] = useState<"move" | "remove" | null>(null);
  const [reportOpen, setReportOpen] = useState(false);

  // "Report to compliance" is universal intake — every staff viewer of the
  // loan page holds Loans.Read, so the menu always renders (never empty);
  // portfolio actions append for Loans.Manage holders only.
  const menuItems = [
    {
      label: "Report to compliance…",
      onSelect: () => setReportOpen(true),
    },
    ...(canManagePortfolio
      ? [
          {
            label: loan.portfolioId
              ? "Move to another portfolio…"
              : "Move to portfolio…",
            onSelect: () => setPortfolioModal("move" as const),
          },
          ...(loan.portfolioId
            ? [
                {
                  label: "Remove from portfolio…",
                  onSelect: () => setPortfolioModal("remove" as const),
                },
              ]
            : []),
        ]
      : []),
  ];

  return (
    <Card>
      <div className="flex flex-col gap-space-3">
        <div className="flex items-start justify-between gap-space-3 flex-wrap">
          <div className="flex flex-col gap-space-1 min-w-0">
            <Link
              to="/portfolio-admin/loans"
              className="inline-flex items-center gap-1 text-caption text-gray-mid hover:text-onyx no-underline"
              data-testid="loan-back-link"
            >
              <ChevronLeft size={14} aria-hidden /> Loan Administration
            </Link>
            <div className="flex items-baseline gap-space-3 flex-wrap">
              <h1
                className="font-display font-bold text-h2 text-onyx m-0 break-words"
                data-testid="loan-title"
              >
                {loan.loanNumber}
              </h1>
              {loan.accountState && (
                <span
                  className={`inline-flex items-center rounded-pill border px-space-2 py-[2px] text-caption font-display uppercase tracking-label ${stateClass}`}
                  data-testid="loan-account-state"
                >
                  {loan.accountState}
                </span>
              )}
              {loan.setupComplete && (
                <Badge status="finalized" data-testid="loan-setup-complete-badge">
                  Setup complete
                </Badge>
              )}
              {loan.closingBypassNote && (
                <Badge
                  status="review"
                  data-testid="loan-bypass-badge"
                  title={loan.closingBypassNote}
                >
                  Bypassed test loan
                </Badge>
              )}
            </div>
            <div className="text-small text-gray-mid">
              Current balance · {formatCurrency(loan.currentBalance)}
              {loan.dealId && (
                <>
                  {" · "}
                  <Link
                    to={`/deal-flow/deals/${loan.dealId}`}
                    className="text-gold-shadow hover:underline"
                    data-testid="loan-deal-link"
                  >
                    {loan.dealNumber ? `Deal ${loan.dealNumber}` : "View originating deal"}
                  </Link>
                </>
              )}
              {loan.portfolioId && loan.portfolioName && (
                <>
                  {" · "}
                  <Link
                    to={`/portfolio-admin/portfolios/${loan.portfolioId}`}
                    className="text-gold-shadow hover:underline"
                    data-testid="loan-portfolio-link"
                  >
                    {loan.portfolioName}
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-space-2">
            <div className="flex items-center gap-space-2">
              <div className="text-caption text-gray-mid uppercase tracking-label">
                Assignments
              </div>
              <OverflowMenu
                label="Loan actions"
                data-testid="loan-header-menu"
                items={menuItems}
              />
            </div>
            <SevenPillAssignmentWidget loanId={loan.id} />
            {canInitiateReunderwriting && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setLaunchOpen(true)}
                data-testid="loan-send-to-reunderwriting"
              >
                <RefreshCw size={14} aria-hidden /> Send to reunderwriting
              </Button>
            )}
          </div>
        </div>
      </div>

      <ReportToComplianceModal
        loanId={loan.id}
        open={reportOpen}
        onClose={() => setReportOpen(false)}
      />

      {portfolioModal && (
        <MoveToPortfolioModal
          loanIds={[loan.id]}
          anyCurrentlyAssigned={loan.portfolioId !== null}
          mode={portfolioModal}
          onClose={() => setPortfolioModal(null)}
          onDone={() => {
            setPortfolioModal(null);
            toast.success(
              portfolioModal === "remove"
                ? "Loan removed from its portfolio."
                : "Loan moved to portfolio.",
            );
          }}
        />
      )}

      {canInitiateReunderwriting && (
        <ReunderwritingLaunchModal
          loanId={loan.id}
          open={launchOpen}
          onClose={() => setLaunchOpen(false)}
        />
      )}
    </Card>
  );
}
